import { t } from '@lingui/macro'
import { Form } from 'formik'
import styled, { useTheme } from 'styled-components'

import { Flex, FlexColumn } from '@/@bothrs-ui/web/components/layout'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'

import type { ReactNode } from 'react'

// ================================ Types ================================

export type DetailInputMode = 'structured' | 'free'

type StructuredObservationItemFormLayoutProps = {
  children: ReactNode
  showDetailInput: boolean
  detailMode: DetailInputMode
  onDetailModeChange: (mode: DetailInputMode) => void
  structuredInput: ReactNode
  freeInput: ReactNode
  submitButton: ReactNode
  showModeSwitch?: boolean
}

// ================================ Component ================================

export const StructuredObservationItemFormLayout = ({
  children,
  showDetailInput,
  detailMode,
  onDetailModeChange,
  structuredInput,
  freeInput,
  submitButton,
  showModeSwitch = true,
}: StructuredObservationItemFormLayoutProps) => {
  const theme = useTheme()

  return (
    <Form
      style={{
        height: '100%',
        // Prevents collapse when the keyboard opens and the dropdown is empty.
        // 450px ≈ typical remaining viewport height on a mid-size phone with keyboard open (~800px screen, ~350px keyboard).
        minHeight: '450px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Flex flexDirection='column' justifyContent='space-between' style={{ height: '100%' }} flexGrow={1}>
        <FlexColumn gap={theme.UI.Spacing.Space6} style={{ paddingBottom: theme.UI.SpacingPx.Space12 }} flexGrow={1}>
          {children}
          {showDetailInput && (detailMode === 'structured' ? structuredInput : freeInput)}
        </FlexColumn>

        <StFormFooter>
          {showDetailInput && showModeSwitch && detailMode === 'structured' && (
            <Span type='BodyMediumRegular' color={theme.Colors.Neutrals.Darker}>
              {t({
                id: 'observation.form.inputMode.notFitting',
                message: 'Past dit schema niet voor jou?',
              })}{' '}
              <StTextLinkButton type='button' onClick={() => onDetailModeChange('free')}>
                {t({
                  id: 'observation.form.inputMode.describeOwn',
                  message: 'Beschrijf het in je eigen woorden',
                })}
              </StTextLinkButton>
              .
            </Span>
          )}
          {showDetailInput && showModeSwitch && detailMode === 'free' && (
            <Span type='BodyMediumRegular' color={theme.Colors.Neutrals.Darker}>
              <StTextLinkButton type='button' onClick={() => onDetailModeChange('structured')}>
                {t({
                  id: 'observation.form.inputMode.switchToStructured',
                  message: 'Terug naar standaard invoer',
                })}
              </StTextLinkButton>
            </Span>
          )}
          {submitButton}
        </StFormFooter>
      </Flex>
    </Form>
  )
}

// ================================ Styled Components ================================

const StFormFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space4};
  text-align: center;
`

const StTextLinkButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: inherit;
  line-height: 1.4;
  color: ${({ theme }) => theme.Colors.Theme.Primary};
  text-decoration: underline;
  text-decoration-color: ${({ theme }) => theme.Colors.Theme.Primary};
`
